import { selectCurrContacts } from '../../../../slices/contactAddressSlice'
import { useSelector, useDispatch } from 'react-redux'
import React, { useRef, useState, useEffect } from 'react'
import ReactCrop, { Crop, PixelCrop, centerCrop, makeAspectCrop } from 'react-image-crop'
import { canvasPreview } from './canvasPreview'
import { useDebounceEffect } from './useDebounceEffect'
import axios from 'axios'
import 'react-image-crop/dist/ReactCrop.css'
import { useNavigate, useParams } from 'react-router-dom'
import { editContactAddress, sortContact } from '../../../../slices/contactAddressSlice'

const centerAspectCrop = (
  mediaWidth: number,
  mediaHeight: number,
  aspect: number = 1
) => {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  )
}

const CropImageUploader = () => {
  const [imgSrc, setImgSrc] = useState<string>('')
  const previewCanvasRef = useRef<HTMLCanvasElement>(null)
  const imgRef = useRef<HTMLImageElement>(null)
  const [crop, setCrop] = useState<Crop>()
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>()
  const [mimeType, setMimeType] = useState<string>('image/png')

  const navigate = useNavigate()
  const dispatch = useDispatch()

  const { id } = useParams()

  const contactAddress = useSelector(selectCurrContacts)
  const selectedAddress = contactAddress.find((c) => c.id === Number(id))

  useEffect(() => {
    if (selectedAddress?.profileImage) {
      setImgSrc(`data:${mimeType};base64,${selectedAddress.profileImage}`)
    }
  }, [selectedAddress, mimeType])

  useDebounceEffect(
    async () => {
      if (
        completedCrop?.width &&
        completedCrop?.height &&
        imgRef.current &&
        previewCanvasRef.current
      ) {
        canvasPreview(imgRef.current, previewCanvasRef.current, completedCrop, 1, 0)
      }
    },
    100,
    [completedCrop]
  )

  const onSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setCrop(undefined)
      const reader = new FileReader()
      reader.addEventListener('load', () => setImgSrc(reader.result?.toString() || ''))
      reader.readAsDataURL(e.target.files[0])
    }
  }

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget
    setCrop(centerAspectCrop(width, height))
  }

  const onSubmitCropImage = async () => {
    if (!previewCanvasRef.current) {
      throw new Error('Crop canvas does not exist')
    }

    const img = previewCanvasRef.current.toDataURL()
    const base64Image = img.slice(img.indexOf('base64,') + 7)
    const extractedMimeType = img.slice(img.indexOf('data:') + 5, img.indexOf(';'))

    setMimeType(extractedMimeType)

    const data = {
      img: base64Image,
      mimeType: extractedMimeType,
    }

    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/uploadfile/${id}`,
        data,
        {
          withCredentials: true,
        }
      )
      console.log('File uploaded:', response.data)
      const { data: contactAddress } = response
      dispatch(editContactAddress(contactAddress))
      dispatch(sortContact({ sortOption: 'lastNameKana', orderAsc: true }))
    } catch (err: any) {
      console.error('File upload failed:', err.response)
      if (err.response.status === 401) {
        alert('セッションが切れています。再度ログインしなおしてください。')
        navigate('/login')
      }
    }
    navigate('/contactAddress')
  }

  return (
    <div className="App">
      <h2>プロフィール画像アップロード</h2>
      <div className="stack">
        <input type="file" accept="image/*" onChange={onSelectFile} />
      </div>

      {!!imgSrc && (
        <ReactCrop
          crop={crop}
          onChange={(_, percentCrop) => setCrop(percentCrop)}
          onComplete={(c) => setCompletedCrop(c)}
          aspect={1}
        >
          <img ref={imgRef} alt="Crop me" src={imgSrc} onLoad={onImageLoad} />
        </ReactCrop>
      )}

      {!!completedCrop && (
        <>
          <div>
            <canvas
              ref={previewCanvasRef}
              style={{
                border: '1px solid black',
                objectFit: 'contain',
                width: completedCrop.width,
                height: completedCrop.height,
              }}
            />
          </div>
          <button type="button" className="submitButton" onClick={onSubmitCropImage}>
            アップロード
          </button>
        </>
      )}
    </div>
  )
}

export default CropImageUploader
